import {
    buildFixedFooterContent,
    FIXED_FOOTER_TOKENS,
    applyFixedFooterPreviewToHtml,
    restoreFixedFooterTokensInHtml,
} from './fixedFooter';

export { buildFixedFooterContent };

export const FIXED_FOOTER_BLOCK_KEY = 'fixed-footer';

export const TOPOL_FIXED_FOOTER_EDITOR_ROLE = 'reader';

export const TOPOL_FIXED_FOOTER_LOCKED_THEME_COLORS = {
    'locked-structure': 'transparent',
    'locked-content': 'transparent',
};

const TOPOL_CONTAINER_WIDTH = 600;

const getFooterContentForEditor = (previewValues) => {
    if (!previewValues) {
        return buildFixedFooterContent();
    }

    return applyFixedFooterPreviewToHtml(buildFixedFooterContent(), previewValues);
};

const isFixedFooterBlock = (block) => {
    if (block?.tagName !== 'mj-raw') {
        return false;
    }
    if (block?.custom === true && block?.key === FIXED_FOOTER_BLOCK_KEY) {
        return true;
    }
    return (
        typeof block?.content === 'string' &&
        block.content.includes(FIXED_FOOTER_TOKENS.senderName) &&
        block.content.includes(FIXED_FOOTER_TOKENS.senderAddress)
    );
};

const getFixedFooterBlockNode = (previewValues) => ({
    tagName: 'mj-raw',
    attributes: {
        containerWidth: TOPOL_CONTAINER_WIDTH,
        'background-color': 'transparent',
    },
    content: getFooterContentForEditor(previewValues),
    custom: true,
    key: FIXED_FOOTER_BLOCK_KEY,
    locked: true,
});

const getEmptyContentSection = () => ({
    tagName: 'mj-section',
    attributes: {
        containerWidth: TOPOL_CONTAINER_WIDTH,
    },
    layout: 1,
    children: [
        {
            tagName: 'mj-column',
            children: [],
        },
    ],
});

const getFixedFooterSection = (previewValues) => ({
    tagName: 'mj-section',
    attributes: {
        containerWidth: TOPOL_CONTAINER_WIDTH,
        locked: true,
    },
    layout: 1,
    children: [
        {
            tagName: 'mj-column',
            children: [getFixedFooterBlockNode(previewValues)],
        },
    ],
});

const sectionContainsFooter = (section) =>
    section?.tagName === 'mj-section' &&
    section.children?.some((column) => column.children?.some(isFixedFooterBlock));

const hasEditableSection = (sections) =>
    (sections || []).some(
        (section) => section?.tagName === 'mj-section' && !sectionContainsFooter(section),
    );

const parseTopolConfig = (config) => {
    if (config == null || config === '') {
        return null;
    }

    if (typeof config === 'string') {
        try {
            return JSON.parse(config);
        } catch {
            return null;
        }
    }

    if (typeof config === 'object') {
        return config;
    }

    return null;
};

const hasTopolBodyContent = (template) => {
    const body = template?.children?.find((child) => child?.tagName === 'mj-body');
    return Array.isArray(body?.children) && body.children.length > 0;
};

const getDefaultTopolTemplate = (previewValues) => ({
    tagName: 'mj-global-style',
    attributes: {
        containerWidth: TOPOL_CONTAINER_WIDTH,
    },
    children: [
        {
            tagName: 'mj-body',
            attributes: {
                containerWidth: TOPOL_CONTAINER_WIDTH,
            },
            children: [getEmptyContentSection(), getFixedFooterSection(previewValues)],
        },
    ],
});

export const ensureFixedFooterLocked = (template, previewValues) => {
    if (!template || typeof template !== 'object') {
        return template;
    }

    const nextTemplate = JSON.parse(JSON.stringify(template));
    const body = nextTemplate.children?.find((child) => child?.tagName === 'mj-body');

    if (!body) {
        return nextTemplate;
    }

    let hasFooter = false;

    body.children = (body.children || []).map((section) => {
        if (section?.tagName !== 'mj-section') {
            return section;
        }

        const nextSection = {
            ...section,
            children: (section.children || []).map((column) => ({
                ...column,
                children: (column.children || []).map((block) => {
                    if (!isFixedFooterBlock(block)) {
                        return block;
                    }

                    hasFooter = true;
                    return getFixedFooterBlockNode(previewValues);
                }),
            })),
        };

        const containsFooter = nextSection.children?.some((column) =>
            column.children?.some(isFixedFooterBlock),
        );

        if (containsFooter) {
            nextSection.attributes = {
                ...nextSection.attributes,
                locked: true,
                'background-color': 'transparent',
            };
        }

        return nextSection;
    });

    if (!hasFooter) {
        const existingSections = body.children || [];
        body.children = hasEditableSection(existingSections)
            ? [...existingSections, getFixedFooterSection(previewValues)]
            : [...existingSections, getEmptyContentSection(), getFixedFooterSection(previewValues)];
    } else if (!hasEditableSection(body.children)) {
        body.children = [getEmptyContentSection(), ...(body.children || [])];
    }

    return nextTemplate;
};

/** JSON con tokens; si hay preview, el bloque footer muestra valores sustituidos. */
export const resolveTopolConfigForLoad = (config, previewValues) => {
    const parsed = parseTopolConfig(config);

    if (parsed && hasTopolBodyContent(parsed)) {
        return ensureFixedFooterLocked(parsed, previewValues);
    }

    return getDefaultTopolTemplate(previewValues);
};

export const wrapTopolSaveWithFixedFooter =
    (onSave, previewValues) =>
    (json, html, ...rest) => {
        const persistedJson = ensureFixedFooterLocked(json, undefined);
        const persistedHtml = restoreFixedFooterTokensInHtml(html, previewValues);
        onSave?.(persistedJson, persistedHtml, html, ...rest);
    };
